import { z } from "zod";

/**
 * Department overview-page builder schemas.
 *
 * The overview page is a JSON document — a list of grid items keyed by
 * `i` (uuid) with their react-grid-layout coordinates and a typed widget
 * payload. Widget props are validated as a Zod discriminated union; any
 * future widget type added to `WIDGET_TYPES` must also gain a branch in
 * `WidgetSchema`.
 *
 * Two surfaces consume this:
 *   - the read-only renderer on the public department page, and
 *   - the drag-and-resize editor available to members holding
 *     `manage_department` on that department.
 *
 * The API persists two layouts per department: `draftLayout` (mutated
 * freely from the editor) and `publishedLayout` (the public version).
 */

export const WIDGET_TYPES = [
  "heading",
  "richText",
  "image",
  "carousel",
  "card",
  "cardGrid",
  "rankLadder",
  "subdivisionList",
  "statCallouts",
  "embed",
  "html",
  "divider",
  "callout",
  "button",
] as const;

export const WidgetTypeSchema = z.enum(WIDGET_TYPES);
export type WidgetType = z.infer<typeof WidgetTypeSchema>;

// ── Reusable bits ───────────────────────────────────────────────────────────

const HexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, "Must be a hex color (#rrggbb or #rrggbbaa)");

/**
 * Restricts URLs to https / data:image / relative paths. Keeps the editor
 * from being weaponised into an open redirect vector.
 */
const SafeUrl = z
  .string()
  .max(2048)
  .refine(
    (s) => {
      const t = s.trim();
      if (t.length === 0) return false;
      if (t.startsWith("/")) return true;
      if (t.startsWith("data:image/")) return true;
      // Avoid relying on a globally-typed `URL` constructor (the shared
      // package has no DOM lib). A pragmatic regex is enough — the server
      // re-validates everything that hits the wire.
      return /^https?:\/\/[^\s]+$/i.test(t);
    },
    { message: "Must be an https URL, a /relative path, or a data:image URI" },
  );

/** Allowlist for the `embed` widget — kept tight on purpose. */
export const EMBED_PROVIDERS = ["youtube", "vimeo", "discord", "spotify", "twitch"] as const;
export const EmbedProviderSchema = z.enum(EMBED_PROVIDERS);
export type EmbedProvider = z.infer<typeof EmbedProviderSchema>;

// ── Per-widget prop schemas ─────────────────────────────────────────────────

export const HeadingPropsSchema = z.object({
  text: z.string().min(1).max(280),
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).default(2),
  align: z.enum(["left", "center", "right"]).default("left"),
  color: HexColor.optional(),
});

export const RichTextPropsSchema = z.object({
  /** Plain text or simple Markdown. Rendered with react-markdown + GFM. */
  markdown: z.string().max(20_000).default(""),
  align: z.enum(["left", "center", "right"]).default("left"),
});

export const ImagePropsSchema = z.object({
  url: SafeUrl,
  alt: z.string().max(255).default(""),
  fit: z.enum(["cover", "contain", "fill"]).default("cover"),
  rounded: z.boolean().default(true),
  caption: z.string().max(255).optional(),
});

export const CarouselPropsSchema = z.object({
  images: z
    .array(
      z.object({
        url: SafeUrl,
        alt: z.string().max(255).default(""),
        caption: z.string().max(255).optional(),
      }),
    )
    .min(1)
    .max(20),
  autoplay: z.boolean().default(true),
  intervalMs: z.number().int().min(1500).max(30_000).default(5000),
  fit: z.enum(["cover", "contain"]).default("cover"),
});

export const CardPropsSchema = z.object({
  title: z.string().max(255).default(""),
  body: z.string().max(2000).default(""),
  imageUrl: SafeUrl.optional(),
  /** Tabler icon name, e.g. "shield". */
  icon: z.string().max(64).optional(),
  accentColor: HexColor.optional(),
  link: z.object({ label: z.string().max(64), url: SafeUrl }).optional(),
});

export const CardGridPropsSchema = z.object({
  columns: z.number().int().min(1).max(4).default(2),
  cards: z
    .array(
      CardPropsSchema.omit({ link: true }).extend({
        link: z.object({ label: z.string().max(64), url: SafeUrl }).optional(),
      }),
    )
    .max(24),
});

export const RankLadderPropsSchema = z.object({
  showHolders: z.boolean().default(false),
  /** Empty = include all ranks. */
  rankIds: z.array(z.number().int().positive()).default([]),
});

export const SubdivisionListPropsSchema = z.object({
  showLogos: z.boolean().default(true),
  /** Empty = include all active subdivisions. */
  subdivisionIds: z.array(z.number().int().positive()).default([]),
});

export const StatCalloutsPropsSchema = z.object({
  stats: z
    .array(
      z.object({
        kind: z.enum(["totalMembers", "activeMembers", "rankCount", "subdivisionCount", "custom"]),
        label: z.string().max(64),
        /** Required when kind === "custom" — frozen value to display. */
        value: z.string().max(64).optional(),
        color: HexColor.optional(),
      }),
    )
    .min(1)
    .max(8),
});

export const EmbedPropsSchema = z.object({
  provider: EmbedProviderSchema,
  /** Provider-specific id (YouTube videoId, Discord guild id, …). */
  refId: z.string().min(1).max(128),
  aspectRatio: z.enum(["16/9", "4/3", "1/1"]).default("16/9"),
});

export const HtmlPropsSchema = z.object({
  /** Sanitised on server before persisting and on render. */
  html: z.string().max(50_000).default(""),
});

export const DividerPropsSchema = z.object({
  thickness: z.number().int().min(1).max(8).default(1),
  color: HexColor.optional(),
});

export const CalloutPropsSchema = z.object({
  variant: z.enum(["info", "success", "warning", "danger"]).default("info"),
  title: z.string().max(120).optional(),
  body: z.string().max(2000),
  icon: z.string().max(64).optional(),
});

export const ButtonPropsSchema = z.object({
  label: z.string().min(1).max(64),
  url: SafeUrl,
  variant: z.enum(["filled", "outline", "subtle"]).default("filled"),
  color: z.string().max(32).optional(),
  fullWidth: z.boolean().default(false),
  icon: z.string().max(64).optional(),
});

// ── Discriminated widget union ──────────────────────────────────────────────

export const WidgetSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("heading"), props: HeadingPropsSchema }),
  z.object({ type: z.literal("richText"), props: RichTextPropsSchema }),
  z.object({ type: z.literal("image"), props: ImagePropsSchema }),
  z.object({ type: z.literal("carousel"), props: CarouselPropsSchema }),
  z.object({ type: z.literal("card"), props: CardPropsSchema }),
  z.object({ type: z.literal("cardGrid"), props: CardGridPropsSchema }),
  z.object({ type: z.literal("rankLadder"), props: RankLadderPropsSchema }),
  z.object({ type: z.literal("subdivisionList"), props: SubdivisionListPropsSchema }),
  z.object({ type: z.literal("statCallouts"), props: StatCalloutsPropsSchema }),
  z.object({ type: z.literal("embed"), props: EmbedPropsSchema }),
  z.object({ type: z.literal("html"), props: HtmlPropsSchema }),
  z.object({ type: z.literal("divider"), props: DividerPropsSchema }),
  z.object({ type: z.literal("callout"), props: CalloutPropsSchema }),
  z.object({ type: z.literal("button"), props: ButtonPropsSchema }),
]);
export type Widget = z.infer<typeof WidgetSchema>;

// ── Layout document ─────────────────────────────────────────────────────────

/**
 * One react-grid-layout cell. The grid is 12 columns wide; `x`, `w` are in
 * column units, `y`, `h` in row units (rowHeight is fixed by the renderer).
 */
export const OverviewLayoutItemSchema = z.object({
  i: z.string().min(1).max(64),
  x: z.number().int().min(0).max(12),
  y: z.number().int().min(0).max(10_000),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(60),
  minW: z.number().int().min(1).max(12).optional(),
  minH: z.number().int().min(1).max(60).optional(),
  /** Optional per-item CSS background tint (hex). */
  background: HexColor.optional(),
  widget: WidgetSchema,
});
export type OverviewLayoutItem = z.infer<typeof OverviewLayoutItemSchema>;

export const OVERVIEW_LAYOUT_VERSION = 1 as const;

export const OverviewLayoutSchema = z
  .object({
    version: z.literal(OVERVIEW_LAYOUT_VERSION),
    items: z.array(OverviewLayoutItemSchema).max(200),
    background: z
      .object({
        color: HexColor.optional(),
        imageUrl: SafeUrl.optional(),
      })
      .optional(),
  })
  .refine(
    (l) => {
      // Item ids must be unique.
      const seen = new Set<string>();
      for (const it of l.items) {
        if (seen.has(it.i)) return false;
        seen.add(it.i);
      }
      return true;
    },
    { message: "Layout item ids must be unique" },
  );
export type OverviewLayout = z.infer<typeof OverviewLayoutSchema>;

export const EMPTY_OVERVIEW_LAYOUT: OverviewLayout = {
  version: OVERVIEW_LAYOUT_VERSION,
  items: [],
};

// ── API response shapes ─────────────────────────────────────────────────────

export const OverviewPageSchema = z.object({
  departmentId: z.number().int(),
  publishedLayout: OverviewLayoutSchema.nullable(),
  publishedAt: z.string().nullable(),
  publishedByName: z.string().nullable(),
});
export type OverviewPage = z.infer<typeof OverviewPageSchema>;

export const OverviewDraftSchema = z.object({
  departmentId: z.number().int(),
  draftLayout: OverviewLayoutSchema.nullable(),
  publishedLayout: OverviewLayoutSchema.nullable(),
  draftUpdatedAt: z.string().nullable(),
  draftUpdatedByName: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedByName: z.string().nullable(),
  /** True iff draft is non-null AND differs from publishedLayout. */
  hasUnpublishedChanges: z.boolean(),
});
export type OverviewDraft = z.infer<typeof OverviewDraftSchema>;

export const OverviewRevisionSchema = z.object({
  id: z.number().int(),
  departmentId: z.number().int(),
  layout: OverviewLayoutSchema,
  publishedAt: z.string(),
  publishedByName: z.string().nullable(),
  note: z.string().nullable(),
});
export type OverviewRevision = z.infer<typeof OverviewRevisionSchema>;

export const SaveOverviewDraftBodySchema = z.object({
  layout: OverviewLayoutSchema,
});
export type SaveOverviewDraftBody = z.infer<typeof SaveOverviewDraftBodySchema>;

export const PublishOverviewBodySchema = z
  .object({
    note: z.string().max(255).optional(),
  })
  .optional();
export type PublishOverviewBody = z.infer<typeof PublishOverviewBodySchema>;
